import React, { useEffect, useState } from 'react';
import { Dialog } from '@reach/dialog';
import GenericTable from '../../components/GenericTable';
import { useBadToolSerials } from '../../context/BadToolSerials';
import { useToolType } from '../../context/ToolTypes';
import { useUser } from '../../context/User';
import { Loading } from '../../components/Loading';
import FieldRow from '../../components/FieldRow';
import LoaderButton from '../../components/LoaderButton';
import { TextOnly } from '../../components/Text';
import { toast } from 'react-toastify';
import { formatDateTime2Lines } from '../../libs/utils';

interface BadToolSerialsProps {
  handleShowAlert: (
    alertTitle: string,
    alertMessage: string | JSX.Element,
    stringify?: boolean
  ) => void;
}

const BadToolSerials: React.FC<BadToolSerialsProps> = ({ handleShowAlert }) => {
  const { user } = useUser();
  const { areBadToolSerialsLoading, badToolSerials, showDeleted, setShowDeleted, handleRefresh, handleCreate, handleUpdate, handleRemove } = useBadToolSerials();
  const { toolTypes } = useToolType();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [selectedSerial, setSelectedSerial] = useState<any>(null);

  const [serials, setSerials] = useState<any[]>(badToolSerials ?? []);

  const [toolManufacturerUUID, setToolManufacturerUUID] = useState<string>('');
  const [toolSerial, setToolSerial] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [toolModelUUID, setToolModelUUID] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [changesMade, setChangesMade] = useState<boolean>(false);

  const [manufacturersOptions, setManufacturersOptions] = useState<any>(null);
  const [modelOptions, setModelOptions] = useState<any>(null);
  const [currentManufacturer, setCurrentManufacturer] = useState<string>('');
  const [currentModel, setCurrentModel] = useState<string>('');

  useEffect(() => {
    if (!toolTypes) {
      setManufacturersOptions(null);
      return;
    }

    const options = toolTypes.map((m: any, i: number) => {
      const displayName = m.toolManufacturer;
      return (
        <option key={i} value={m.toolManufacturer}>
          {displayName}
        </option>
      );
    });

    setManufacturersOptions(options);
  }, [toolTypes]);

  useEffect(() => {
    if (!currentManufacturer || !toolTypes) {
      setModelOptions(null);
      return;
    }

    const manufacturer = toolTypes.find((m: any) => m.toolManufacturer === currentManufacturer);
    if (!manufacturer) {
      setModelOptions(null);
      return;
    }

    const models = manufacturer.toolModelList.map((tool: any, i: number) => (
      <option key={i} value={tool.toolModel}>
        {tool.toolModel}
      </option>
    ));

    setModelOptions(models);
  }, [currentManufacturer, toolTypes]);

  useEffect(() => {
    if (!showEditModal && !showCreateModal) {
      setChangesMade(false);
      return;
    }

    if (showEditModal && selectedSerial) {
      const hasChanges = hasFormChanged();

      setChangesMade(hasChanges);
    } else {
      const hasChanges = toolManufacturerUUID.trim() !== '' ||
        toolSerial.trim() !== '' ||
        message.trim() !== '';

      setChangesMade(hasChanges);
    }
  }, [showEditModal, showCreateModal, selectedSerial, toolManufacturerUUID, toolSerial, message, toolModelUUID]);

  const handleManufacturerChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newManufacturer = event.target.value;

    const manufacturer = toolTypes?.find((m: any) => m.toolManufacturer === newManufacturer);
    if (manufacturer) {
      setToolManufacturerUUID(manufacturer.toolManufacturerUUID);
      setCurrentModel('');
      setToolModelUUID('');
    }

    setCurrentManufacturer(newManufacturer);
  };

  const hasFormChanged = () => {
    if (!selectedSerial) return false;

    const currentManufacturerTrimmed = toolManufacturerUUID.trim();
    const currentSerialTrimmed = toolSerial.trim();
    const currentMessageTrimmed = message.trim();
    const currentModelTrimmed = toolModelUUID.trim();

    const originalManufacturer = selectedSerial.toolManufacturerUUID || '';
    const originalSerial = selectedSerial.toolSerial || '';
    const originalMessage = selectedSerial.message || '';
    const originalModel = selectedSerial.toolModelUUID || '';

    return (
      currentManufacturerTrimmed !== originalManufacturer ||
      currentSerialTrimmed !== originalSerial ||
      currentMessageTrimmed !== originalMessage ||
      currentModelTrimmed !== originalModel
    );
  };

  const handleModelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = event.target.value;
    setCurrentModel(newModel);

    const manufacturer = toolTypes?.find((m: any) => m.toolManufacturer === currentManufacturer);
    if (manufacturer) {
      const model = manufacturer.toolModelList.find((t: any) => t.toolModel === newModel);
      if (model) {
        setToolModelUUID(model.toolModelUUID);
      } else {
        setToolModelUUID('');
      }
    }
  };

  const resetFormState = () => {
    setToolManufacturerUUID('');
    setToolSerial('');
    setMessage('');
    setToolModelUUID('');
    setCurrentManufacturer('');
    setCurrentModel('');
    setChangesMade(false);
  };

  const handleCloseFormModal = (isEdit: boolean) => {
    if (changesMade) {
      setShowUnsavedChangesModal(true);
      return;
    }
    closeModal(isEdit);
  };

  const closeModal = (isEdit: boolean) => {
    document.querySelector('.c-modal-slider')?.classList.add('closed');
    setTimeout(() => {
      if (isEdit) {
        setShowEditModal(false);
        setSelectedSerial(null);
      } else {
        setShowCreateModal(false);
      }
      resetFormState();
    }, 350);
  };

  const handleCloseUnsavedChangesModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowUnsavedChangesModal(false);
    }, 350);
  }

  const handleDiscardChanges = () => {
    const isEdit = showEditModal;
    closeModal(isEdit);
    handleCloseUnsavedChangesModal();
  };

  const handleOpenCreateModal = () => {
    resetFormState();
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (serial: any) => {
    setSelectedSerial(serial);
    setToolManufacturerUUID(serial.toolManufacturerUUID || '');
    setToolSerial(serial.toolSerial || '');
    setMessage(serial.message || '');
    setToolModelUUID(serial.toolModelUUID || '');

    const manufacturer = toolTypes?.find((m: any) => m.toolManufacturerUUID === serial.toolManufacturerUUID);
    if (manufacturer) {
      setCurrentManufacturer(manufacturer.toolManufacturer);
      const model = serial.toolModelUUID
        ? manufacturer.toolModelList.find((t: any) => t.toolModelUUID === serial.toolModelUUID)
        : null;
      setCurrentModel(model?.toolModel || '');
    } else {
      setCurrentManufacturer('');
      setCurrentModel('');
    }

    setShowEditModal(true);
  };

  const handleCloseDeleteModal = () => {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      setShowDeleteModal(false);
      setSelectedSerial(null);
    }, 350);
  };

  const handleOpenDeleteModal = (serial: any) => {
    setSelectedSerial(serial);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
    return toolManufacturerUUID.trim() !== '' && toolSerial.trim() !== '' && message.trim() !== '' && currentManufacturer.trim() !== '';
  };

  const isDuplicateToolSerial = () => {
    const currentManufacturer = toolManufacturerUUID.trim();
    const currentSerial = toolSerial.trim();
    const currentModel = toolModelUUID.trim();

    const existingSerial = (badToolSerials ?? []).find(serial => {
      if (serial.deleted) return false;

      if (selectedSerial && serial.badToolSerialUUID === selectedSerial.badToolSerialUUID) {
        return false;
      }

      const existingManufacturer = serial.toolManufacturerUUID?.trim() || '';
      const existingSerialVal = serial.toolSerial?.trim() || '';
      const existingModel = serial.toolModelUUID?.trim() || '';

      return existingManufacturer === currentManufacturer && existingSerialVal === currentSerial && currentModel === existingModel;
    });

    return !!existingSerial;
  };

  const isValidBadToolSerial = () => {
    if (!selectedSerial) return false;

    if (!toolManufacturerUUID.trim() || !toolSerial.trim() || !message.trim() || !currentManufacturer.trim()) {
      return false;
    }

    return hasFormChanged();
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const response = await handleCreate(
      toolManufacturerUUID.trim(),
      toolSerial.trim(),
      message.trim(),
      toolModelUUID.trim() || undefined
    );

    if (response?.error) {
      handleShowAlert('Error creating Blocked Tool Serial', response.error);
    } else {
      toast.success('Successfully created blocked tool serial!', { containerId: 'standard' });
      closeModal(false);
    }

    setTimeout(() => {
      setIsSubmitting(false);
    }, 350);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidBadToolSerial() || !selectedSerial) {
      return;
    }

    setIsSubmitting(true);

    const response = await handleUpdate(
      selectedSerial.badToolSerialUUID,
      toolManufacturerUUID.trim(),
      toolSerial.trim(),
      message.trim(),
      toolModelUUID.trim() || undefined
    );

    if (response?.error) {
      handleShowAlert('Error updating Blocked Tool Serial', response.error);
    } else {
      toast.success('Successfully edited blocked tool serial!', { containerId: 'standard' });
      closeModal(true);
    }

    setTimeout(() => {
      setIsSubmitting(false);
    }, 350);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSerial) return;

    setIsSubmitting(true);

    const response = await handleRemove(selectedSerial.badToolSerialUUID);

    if (response?.error) {
      handleShowAlert('Error removing Blocked Tool Serial', response.error);
    } else {
      toast.success('Successfully removed blocked tool serial!', { containerId: 'standard' });
      handleCloseDeleteModal();
    }

    setTimeout(() => {
      setIsSubmitting(false);
    }, 350);
  };

  const refreshBadToolSerials = async () => {
    setIsLoading(true);

    const response = await handleRefresh(true);

    if (response?.error) {
      handleShowAlert(
        'Error refreshing Blocked Tool Serials',
        response.error
      );
    }

    setIsLoading(false);
    return;
  };

  useEffect(() => {
    const serials = (badToolSerials ?? [])
      .filter(serial => showDeleted || !serial.deleted)
      .map(serial => {
        const manufacturer = toolTypes?.find((m: any) => m.toolManufacturerUUID === serial.toolManufacturerUUID);
        const model = serial.toolModelUUID && manufacturer
          ? manufacturer.toolModelList.find((t: any) => t.toolModelUUID === serial.toolModelUUID)
          : null;
        return {
          ...serial,
          toolManufacturerName: manufacturer?.toolManufacturer || '',
          toolModelName: model?.toolModel || '',
        };
      });
    setSerials(serials);
  }, [badToolSerials, toolTypes, showDeleted]);

  if (!user || !['ADMIN', 'MANAGER'].includes(user.userType)) {
    return <Loading />;
  }

  return (
    <div>
      <div className="l-flex-between l-flex-align-center">
        <h2 className="c-card__title">Blocked Tool Serials</h2>
        <div className='l-flex-align-center'>
          <button
            className="c-btn u-margin-right-large"
            onClick={handleOpenCreateModal}
          >
            <div className="c-btn__inner">
              <span className="c-btn__icon fa fa-plus" title="Create" /> Create
            </div>
          </button>
          <div className="c-btn__inner l-margin-right-10">Show Deleted</div>
          <input
            id="showDeleted"
            type="checkbox"
            name="showDeleted"
            checked={showDeleted}
            onChange={() => setShowDeleted(!showDeleted)}
            className="c-btn__inner u-margin-right-large"
          />
          <button
            className="c-btn-icon"
            onClick={() => refreshBadToolSerials()}
          >
            <div className="c-btn__inner">
              <span className="c-btn__icon fa fa-refresh" title="Refresh" />
            </div>
          </button>
        </div>
      </div>

      <hr />

      <GenericTable
        data={serials}
        filterKeys={[
          'toolSerial',
          'message',
          'toolManufacturerName',
          'toolModelName',
          'createdBy',
        ]}
        columnDefs={[
          {
            Header: 'Tool Manufacturer',
            accessor: 'toolManufacturerName',
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.toolManufacturerName}
                </div>
              );
            },
          },
          {
            Header: 'Tool Model',
            accessor: 'toolModelName',
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.toolModelName}
                </div>
              );
            },
          },
          {
            Header: 'Tool Serial',
            accessor: 'toolSerial',
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.toolSerial}
                </div>
              );
            },
          },
          {
            Header: 'Message',
            accessor: 'message',
            minWidth: 175,
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.message}
                </div>
              );
            },
          },
          {
            Header: 'Created By',
            accessor: 'createdBy',
            minWidth: 175,
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.createdBy}
                </div>
              );
            },
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            maxWidth: 115,
            Cell: (row: { original: any }) => {
              const dateObj = formatDateTime2Lines(row.original.createdOn);

              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  <div>
                    {dateObj.date}
                  </div>
                  <div>
                    {dateObj.time}
                  </div>
                </div>
              );
            },
          },
          ...(showDeleted ? [
          {
            Header: 'Deleted By',
            accessor: 'deletedBy',
            Cell: (row: { original: any }) => {
              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  {row.original.deletedBy}
                </div>
              );
            },
          },
          {
            Header: 'Deleted On',
            accessor: 'deletedOn',
            maxWidth: 115,
            Cell: (row: { original: any }) => {
              if (!row.original.deletedOn) return null;

              const dateObj = formatDateTime2Lines(row.original.deletedOn);

              return (
                <div className={row.original.deleted ? 'u-text-error' : ''}>
                  <div>
                    {dateObj.date}
                  </div>
                  <div>
                    {dateObj.time}
                  </div>
                </div>
              );
            },
          },
          ] : []),
          {
            Header: 'Actions',
            accessor: 'badToolSerialUUID',
            minWidth: 65,
            maxWidth: 65,
            sortable: false,
            filterable: false,
            Cell: (row: { original: any }) => {
              if (row.original.deleted) return null;
              return (
                <div className="l-flex-align-center">
                  <i
                    className="fal fa-pen-to-square clickable"
                    title="Edit"
                    onClick={() => handleOpenEditModal(row.original)}
                  />
                  <i
                    className="fal fa-trash-alt clickable u-margin-left-large"
                    title="Delete"
                    onClick={() => handleOpenDeleteModal(row.original)}
                  />
                </div>
              );
            },
          },
        ]}
        defaultSorted={[
          {
            id: 'toolManufacturerName',
            desc: false,
          },
        ]}
        loadingText={'Loading Blocked Tool Serials...'}
        loading={areBadToolSerialsLoading || isLoading}
        savedColumnsId={'badToolSerialsColumns'}
      />

      <Dialog
        isOpen={showUnsavedChangesModal}
        onDismiss={handleCloseUnsavedChangesModal}
        className="c-modal"
        aria-label="Unsaved Changes"
      >
        <div className="l-flex-column l-flex-align-center">
          <button
            className="c-btn-icon c-modal__close"
            onClick={handleCloseUnsavedChangesModal}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fal fa-times" />
            </div>
          </button>
          <h1 className='c-modal__heading'>Unsaved Changes</h1>
          <div className="c-modal__body u-text-center">
            <div>Are you sure you want to close this form?</div>
            <div className="u-margin-top">Any changes made will be lost!</div>
            <div className="u-margin-top-large l-container-center">
              <button className="c-btn-outline u-margin-right-large" onClick={handleCloseUnsavedChangesModal}>
                Keep Editing
              </button>
              <button className="c-btn" onClick={handleDiscardChanges}>
                Close Without Saving
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showCreateModal}
        onDismiss={() => handleCloseFormModal(false)}
        className="c-modal-slider"
        aria-label="Create Blocked Tool Serial"
      >
        <button
          className="c-btn-icon c-modal-slider__close"
          onClick={() => handleCloseFormModal(false)}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fal fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Create Blocked Tool Serial</h1>

        <div className="c-modal__body">
          <form onSubmit={handleCreateSubmit}>
            <div className="c-field">
              <label htmlFor="toolManufacturer" className="c-field__label">
                <span className="u-text-error">*</span> Manufacturer
              </label>
              <div className="c-select">
                <select
                  id="toolManufacturer"
                  onChange={handleManufacturerChange}
                  value={currentManufacturer || ''}
                >
                  <option value="" disabled>
                    {TextOnly('noneSelected')}
                  </option>
                  {manufacturersOptions}
                </select>
              </div>
            </div>
            <div className="c-field">
              <label htmlFor="toolModel" className="c-field__label">
                Tool Model
              </label>
              <div className="c-select">
                <select
                  id="toolModel"
                  onChange={handleModelChange}
                  value={currentModel || ''}
                  disabled={!currentManufacturer}
                >
                  <option value="">
                    {TextOnly('noneSelected')}
                  </option>
                  {modelOptions}
                </select>
              </div>
            </div>
            <FieldRow
              id="toolSerial"
              title="Tool Serial"
              required
              placeholder="Enter tool serial"
              value={toolSerial}
              onChange={(e) => {
                setToolSerial(e.target.value.toUpperCase());
              }}
            />
            <div className="u-margin-left-small u-margin-bottom">
              <label htmlFor="message" className="c-field__label"><span className="u-text-error">*</span> Message</label>
              <textarea
                id="message"
                className="c-input rejection-textarea"
                placeholder={"Enter message"}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                }}
                rows={3}
              />
            </div>
            <div className="c-field">
              {!isSubmitting && isDuplicateToolSerial() && (
                <div className="text-bold u-text-error u-margin-bottom-large">
                  Tool information already exists in table!
                </div>
              )}
              <LoaderButton
                type="submit"
                disabled={!validateForm() || isDuplicateToolSerial() || isSubmitting}
                isLoading={isSubmitting}
                text="Create"
                loadingText="Creating..."
              />
            </div>
          </form>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditModal}
        onDismiss={() => handleCloseFormModal(true)}
        className="c-modal-slider"
        aria-label="Edit Blocked Tool Serial"
      >
        <button
          className="c-btn-icon c-modal-slider__close"
          onClick={() => handleCloseFormModal(true)}
        >
          <div className="c-btn__inner">
            <i className="c-btn__icon fal fa-times" />
          </div>
        </button>
        <h1 className="c-modal__heading">Edit Blocked Tool Serial</h1>

        <div className="c-modal__body">
          <form onSubmit={handleEditSubmit}>
            <div className="c-field">
              <label htmlFor="toolManufacturerEdit" className="c-field__label">
                <span className="u-text-error">*</span> Manufacturer
              </label>
              <div className="c-select">
                <select
                  id="toolManufacturerEdit"
                  onChange={handleManufacturerChange}
                  value={currentManufacturer || ''}
                >
                  <option value="" disabled>
                    {TextOnly('noneSelected')}
                  </option>
                  {manufacturersOptions}
                </select>
              </div>
            </div>
            <div className="c-field">
              <label htmlFor="toolModelEdit" className="c-field__label">
                Tool Model
              </label>
              <div className="c-select">
                <select
                  id="toolModelEdit"
                  onChange={handleModelChange}
                  value={currentModel || ''}
                  disabled={!currentManufacturer}
                >
                  <option value="">
                    {TextOnly('noneSelected')}
                  </option>
                  {modelOptions}
                </select>
              </div>
            </div>
            <FieldRow
              id="toolSerial"
              title="Tool Serial"
              required
              placeholder="Enter tool serial"
              value={toolSerial}
              onChange={(e) => {
                setToolSerial(e.target.value.toUpperCase());
              }}
            />
            <div className="u-margin-left-small u-margin-bottom">
              <label htmlFor="message" className="c-field__label"><span className="u-text-error">*</span> Message</label>
              <textarea
                id="message"
                className="c-input rejection-textarea"
                placeholder={"Enter message"}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                }}
                rows={3}
              />
            </div>
            <div className="c-field">
              {!isSubmitting && isDuplicateToolSerial() && (
                <div className="text-bold u-text-error u-margin-bottom-large">
                  Tool information already exists in table!
                </div>
              )}
              <LoaderButton
                type="submit"
                disabled={!isValidBadToolSerial() || isDuplicateToolSerial() || isSubmitting}
                isLoading={isSubmitting}
                text="Save Changes"
                loadingText="Saving..."
              />
            </div>
          </form>
        </div>
      </Dialog>

      <Dialog
        isOpen={showDeleteModal}
        onDismiss={handleCloseDeleteModal}
        className="c-modal"
        aria-label="Confirm Delete"
      >
        <div className="l-flex-column l-flex-align-center">
          <button
            className="c-btn-icon c-modal__close"
            onClick={handleCloseDeleteModal}
          >
            <div className="c-btn__inner">
              <i className="c-btn__icon fal fa-times" />
            </div>
          </button>
          <h1 className='c-modal__heading'>Remove Blocked Tool Serial</h1>
          <div className="c-modal__body u-text-center">
            <div>Are you sure you want to remove this blocked tool serial?</div>
            <div className="u-margin-top">This action cannot be undone.</div>
            <div className="u-margin-top-large l-container-center">
              <button
                className="c-btn-outline u-margin-right-large"
                disabled={isSubmitting}
                onClick={handleCloseDeleteModal}
              >
                Cancel
              </button>
              <button
                className="c-btn"
                disabled={isSubmitting}
                onClick={handleDeleteConfirm}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default BadToolSerials;